import {
  DIRECTION,
  IDirection,
  Table,
  TableBody,
  TableCell,
  TablePagination
} from '@sanlam-glacier/app-common/components';
import { Section14TableRow } from './Section14TableRow';
import styles from './section-14-table.module.scss';
import { useEffect, useState } from 'react';
import { Section14TableHeadings } from 'modules/my-practice/types/section-14/section-14.interfaces';
import { Section14TableHeaderConfig } from 'modules/my-practice/types/section-14/section-14-table.types';
import {
  Section14HeaderKeys,
  Section14UrgencyTab
} from 'modules/my-practice/types/section-14/section-14.enums';
import clsx from 'clsx';
import { Section14TableHeader } from './Section14TableHeader';
import { sortByRenewalDate } from 'modules/my-practice/utils/section-14/client-formatter';
import { Section14ClientRenewalModel } from 'bff';

type Section14TableProps = {
  clients: Section14ClientRenewalModel[];
  activeTab: Section14UrgencyTab;
  currentPage: number;
  setActiveTabCurrentPage: (page: number) => void;
};

export const Section14Table = ({
  clients,
  activeTab,
  currentPage,
  setActiveTabCurrentPage
}: Section14TableProps) => {
  const testComponentName = 'Section14Table';

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(currentPage);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleExpand = (clientId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const [section14TableHeaderData, setSection14TableHeaderData] = useState<
    Section14TableHeaderConfig<Section14TableHeadings>[]
  >([]);

  useEffect(() => {
    const intialHeaderData: Section14TableHeaderConfig<Section14TableHeadings>[] = [
      {
        key: Section14HeaderKeys.clientName,
        label: 'Client name',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead, styles.clientNameColumn)
      },
      {
        label: 'Investment details',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead)
      },
      {
        label: 'Investment value',
        align: 'right',
        className: clsx(styles.tableHead, styles.investmentValueColumn)
      },
      {
        label: 'Intermediary fee',
        align: 'right',
        className: clsx(styles.tableHead, styles.intermediaryFeeColumn)
      },
      {
        key: Section14HeaderKeys.feeRenewalDate,
        label: 'Fee renewal date',
        align: 'right',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead, styles.feeRenewalDateColumn)
      },
      {
        label: 'Status',
        className: clsx(styles.tableHead, styles.statusColumn)
      },
      {
        label: 'Renewal form',
        align: 'right',
        className: clsx(styles.tableHead, styles.renewalFormColumn)
      }
    ];
    setPage(currentPage);
    setSection14TableHeaderData(intialHeaderData);
  }, [currentPage]);

  const toggleDirection = (direction: IDirection): IDirection => {
    return direction === DIRECTION.ASC ? DIRECTION.DESC : DIRECTION.ASC;
  };

  const handleSort = (
    property: Section14TableHeaderConfig<Section14TableHeadings>['key'] | string,
    direction: IDirection | undefined
  ) => {
    if (direction) {
      const newDirection = toggleDirection(direction);

      setSection14TableHeaderData((prev) =>
        prev.map((header) => {
          if (header.key === property) {
            return { ...header, direction: newDirection };
          }
          return header;
        })
      );

      switch (property) {
        case Section14HeaderKeys.clientName:
          clients.sort((a, b) =>
            newDirection === DIRECTION.DESC
              ? a.clientName.localeCompare(b.clientName)
              : b.clientName.localeCompare(a.clientName)
          );
          break;
        case Section14HeaderKeys.feeRenewalDate:
          sortByRenewalDate(clients, newDirection);
          break;
        default:
          break;
      }
    }
  };

  const paginatedData = clients.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <>
      <Table data-testid={`${testComponentName}`}>
        <Section14TableHeader
          headers={section14TableHeaderData}
          align={TableCell.ALIGN.LEFT}
          handleSort={handleSort}
        />
        <TableBody>
          {paginatedData.map((row) => (
            <Section14TableRow
              key={row.clientId}
              row={row}
              activeTab={activeTab}
              isExpanded={expandedRows.has(row.clientId)}
              toggleExpand={toggleExpand}
            />
          ))}
        </TableBody>
      </Table>

      <TablePagination
        data-testid={`${testComponentName}TablePagination`}
        count={clients.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(newPage: number) => {
          setActiveTabCurrentPage(newPage);
          setPage(newPage);
        }}
        onRowsPerPageChange={(newPageSize: number) => {
          setPage(0);
          setPageSize(newPageSize);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        closeMenuOnSelection
        showLabels={false}
        dropdownLabel='Clients per page:'
        className={styles.pagination}
      />
    </>
  );
};
